/**
 * pes-powerline-chat.js — PES PowerLine branded chat experience (v1, 2026-08-28)
 *
 * Owns the visible chat bubble + quick panel for portlandiaelectric.supply.
 * The Intercom default launcher is hidden by pes-intercom-unified.js
 * (hide_default_launcher: true); this script renders the branded launcher and
 * routes conversations into the Intercom messenger (app lt1fbeyf).
 *
 * Brand: charcoal #242833, green #11B67B, Poppins/Inter, PES Supply logo.
 * Measurement: guarded dataLayer pushes (chat_opened, chat_quick_action),
 * legacy pes_agent_bubble_shown continuity for the PES Clarity Bridge,
 * Clarity custom events, Klaviyo onsite track when available.
 *
 * Voice rules (shared with pes-intercom-unified.js):
 *   - "169 authorized brands", "trade pricing", "50,000+ SKUs"
 *   - "7-10 business days delivery nationwide"
 *   - NEVER free shipping, NEVER same-day shipping, NEVER "112,000+ products"
 */
(function () {
  'use strict';

  var w = window;
  var d = document;

  var CHARCOAL = '#242833';
  var GREEN = '#11B67B';
  var LOGO_URL = 'https://www.portlandiaelectric.supply/cdn/shop/files/pes_supply_logo_header.png';
  var QUOTE_URL = '/pages/quote';

  if (w.__pesPowerLineLoaded) return;
  w.__pesPowerLineLoaded = true;

  // ------------------------------------------------------------------
  // Measurement helpers (same guarded pattern as the PES Clarity Bridge)
  // ------------------------------------------------------------------
  function safeDataLayerPush(obj) {
    try {
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push(obj);
    } catch (e) {}
  }

  function safeClarity() {
    try {
      if (typeof w.clarity === 'function') {
        w.clarity.apply(null, arguments);
      }
    } catch (e) {}
  }

  function safeKlaviyoTrack(eventName, props) {
    try {
      if (w.klaviyo && typeof w.klaviyo.push === 'function') {
        w.klaviyo.push(['track', eventName, props || {}]);
      } else if (w._learnq && typeof w._learnq.push === 'function') {
        w._learnq.push(['track', eventName, props || {}]);
      }
    } catch (e) {}
  }

  function pageType() {
    var p = w.location.pathname;
    if (p.indexOf('/products/') !== -1) return 'product';
    if (p === '/cart' || p.indexOf('/cart/') !== -1) return 'cart';
    if (p.indexOf('/blogs/') !== -1) return 'article';
    if (p.indexOf('/collections/') !== -1) return 'collection';
    if (p.indexOf('/search') !== -1) return 'search';
    if (p === '/' || p === '') return 'home';
    return 'page';
  }

  function trackOpen() {
    var payload = {
      event: 'chat_opened',
      chat_surface: 'pes_powerline',
      page_type: pageType(),
      page_url: w.location.href
    };
    safeDataLayerPush(payload);
    safeClarity('event', 'pes_powerline_opened');
    safeKlaviyoTrack('PES Chat Opened', {
      surface: 'pes_powerline',
      page_type: payload.page_type,
      page_url: payload.page_url
    });
  }

  function trackAction(action, label) {
    safeDataLayerPush({
      event: 'chat_quick_action',
      chat_surface: 'pes_powerline',
      chat_action: action,
      chat_action_label: label,
      page_type: pageType(),
      page_url: w.location.href
    });
    safeClarity('event', 'pes_powerline_action_' + action);
    safeKlaviyoTrack('PES Chat Quick Action', {
      action: action,
      label: label,
      page_type: pageType(),
      page_url: w.location.href
    });
  }

  // The PES Clarity Bridge fires pes_agent_bubble_shown when the native
  // Intercom launcher iframe first appears. The native launcher is now hidden
  // (PES PowerLine owns the bubble), so fire the same event when OUR launcher
  // becomes visible — keeps the Clarity smart event + dataLayer continuity.
  var bubbleShownFired = false;
  function fireBubbleShown() {
    if (bubbleShownFired) return;
    bubbleShownFired = true;
    safeClarity('event', 'pes_agent_bubble_shown');
    safeDataLayerPush({ event: 'pes_agent_bubble_shown', chat_surface: 'pes_powerline' });
  }

  // ------------------------------------------------------------------
  // Context-aware greeting (mirrors buildContextGreeting in unified.js;
  // prefers the dynamic_greeting Intercom already received)
  // ------------------------------------------------------------------
  function contextGreeting() {
    try {
      if (w.intercomSettings && w.intercomSettings.dynamic_greeting) {
        return w.intercomSettings.dynamic_greeting;
      }
    } catch (e) {}
    var p = w.location.pathname;
    if (p.indexOf('/products/') !== -1) {
      var ptype = '';
      var ld = d.querySelectorAll('script[type="application/ld+json"]');
      for (var i = 0; i < ld.length; i++) {
        try {
          var data = JSON.parse(ld[i].textContent);
          var prod = Array.isArray(data) ? data[0] : data;
          if (prod && prod['@type'] === 'Product') { ptype = prod.category || ''; break; }
        } catch (e) {}
      }
      if (!ptype) {
        /* fallback: collection-level crumb from BreadcrumbList JSON-LD */
        for (var j = 0; j < ld.length; j++) {
          try {
            var bc = JSON.parse(ld[j].textContent);
            var bcn = Array.isArray(bc) ? bc[0] : bc;
            if (bcn && bcn['@type'] === 'BreadcrumbList' && bcn.itemListElement && bcn.itemListElement.length > 2) {
              var crumb = bcn.itemListElement[bcn.itemListElement.length - 2];
              var cname = (crumb && (crumb.name || (crumb.item && crumb.item.name))) || '';
              if (cname && cname.toLowerCase() !== 'home') {
                cname = cname.charAt(0).toLowerCase() + cname.slice(1);
                if (cname.length > 3 && cname.slice(-1) === 's' && cname.slice(-2) !== 'ss') cname = cname.slice(0, -1);
                ptype = cname;
                break;
              }
            }
          } catch (e) {}
        }
      }
      var what = ptype ? ('this ' + ptype) : 'this product';
      return 'Questions about ' + what + '? Sizing, compatibility, bulk pricing — ask away.';
    }
    if (p === '/cart' || p.indexOf('/cart/') !== -1) {
      return 'Almost there — need a formal quote, freight estimate, or payment terms before you check out? We can sort it here.';
    }
    if (p.indexOf('/blogs/') !== -1) {
      var og = d.querySelector('meta[property="og:title"]');
      var topic = og ? (og.getAttribute('content') || '') : '';
      if (topic.length > 60) topic = topic.slice(0, 60);
      return topic ? ('Reading "' + topic + '"? We can point you to the right gear or answer spec questions.')
                   : 'Digging into our guides? We can point you to the right gear or answer spec questions.';
    }
    if (p.indexOf('/collections/') !== -1) {
      return 'Browsing the catalog? Tell us what you are building and we will pull trade pricing, lead times, and freight.';
    }
    if (p.indexOf('/search') !== -1) {
      return 'Hunting for a part? Send the manufacturer and part number — we source from 300+ manufacturers.';
    }
    return 'PES Supply — wholesale electrical, solar, and building materials from 169 authorized brands. Trade pricing with full OEM warranties. What are you building?';
  }

  function productContextLine() {
    if (w.location.pathname.indexOf('/products/') === -1) return '';
    var title = '';
    var og = d.querySelector('meta[property="og:title"]');
    if (og) title = og.getAttribute('content') || '';
    return title ? ('Product: ' + title + '\nURL: ' + w.location.href + '\n\n') : '';
  }

  // ------------------------------------------------------------------
  // Intercom bridge
  // ------------------------------------------------------------------
  function openMessenger(prefill) {
    closePanel();
    if (typeof w.Intercom === 'function') {
      try {
        w.Intercom('show');
        if (prefill) {
          setTimeout(function () {
            try { w.Intercom('showNewMessage', prefill); } catch (e) {}
          }, 400);
        }
      } catch (e) {}
    } else {
      // Intercom blocked/unavailable — fall back to the quote page
      w.location.href = QUOTE_URL;
    }
  }

  // ------------------------------------------------------------------
  // Styles (injected once)
  // ------------------------------------------------------------------
  var css = [
    '#pes-powerline-launcher{position:fixed;bottom:20px;right:20px;z-index:2147483646;',
    'width:60px;height:60px;border-radius:50%;border:none;cursor:pointer;',
    'background:' + CHARCOAL + ';box-shadow:0 6px 20px rgba(0,0,0,0.28),0 0 0 2px ' + GREEN + ';',
    'display:flex;align-items:center;justify-content:center;padding:0;',
    'transition:transform .18s ease,box-shadow .18s ease;}',
    '#pes-powerline-launcher:hover{transform:translateY(-2px) scale(1.04);',
    'box-shadow:0 10px 26px rgba(0,0,0,0.32),0 0 0 2px ' + GREEN + ';}',
    '#pes-powerline-launcher:focus-visible{outline:3px solid ' + GREEN + ';outline-offset:3px;}',
    '#pes-powerline-launcher .pes-pl-bolt{width:26px;height:26px;fill:none;stroke:' + GREEN + ';stroke-width:2.2;stroke-linecap:round;stroke-linejoin:round;}',
    '#pes-powerline-launcher .pes-pl-dot{position:absolute;top:2px;right:2px;width:12px;height:12px;',
    'border-radius:50%;background:' + GREEN + ';border:2px solid #fff;}',
    '#pes-powerline-panel{position:fixed;bottom:92px;right:20px;z-index:2147483646;',
    'width:360px;max-width:calc(100vw - 32px);border-radius:16px;overflow:hidden;',
    'background:#fff;box-shadow:0 18px 48px rgba(0,0,0,0.30);',
    'font-family:Poppins,Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;',
    'opacity:0;transform:translateY(12px);pointer-events:none;',
    'transition:opacity .2s ease,transform .2s ease;}',
    '#pes-powerline-panel.pes-pl-open{opacity:1;transform:translateY(0);pointer-events:auto;}',
    '#pes-powerline-panel .pes-pl-head{background:' + CHARCOAL + ';padding:16px 18px 14px;position:relative;}',
    '#pes-powerline-panel .pes-pl-logo{height:22px;width:auto;display:block;margin-bottom:8px;}',
    '#pes-powerline-panel .pes-pl-title{color:#fff;font-size:16px;font-weight:600;line-height:1.2;margin:0;}',
    '#pes-powerline-panel .pes-pl-sub{color:rgba(255,255,255,0.72);font-size:12px;margin:2px 0 0;font-weight:400;}',
    '#pes-powerline-panel .pes-pl-close{position:absolute;top:10px;right:12px;background:none;border:none;',
    'color:rgba(255,255,255,0.7);font-size:20px;line-height:1;cursor:pointer;padding:4px;}',
    '#pes-powerline-panel .pes-pl-close:hover{color:#fff;}',
    '#pes-powerline-panel .pes-pl-body{padding:16px 18px 14px;}',
    '#pes-powerline-panel .pes-pl-greet{font-size:13.5px;line-height:1.5;color:#333a45;margin:0 0 14px;}',
    '#pes-powerline-panel .pes-pl-chips{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px;}',
    '#pes-powerline-panel .pes-pl-chip{border:1px solid #d8dde3;border-radius:10px;background:#f7f8f9;',
    'color:' + CHARCOAL + ';font-family:inherit;font-size:12.5px;font-weight:500;',
    'padding:9px 10px;cursor:pointer;text-align:left;transition:border-color .15s,background .15s;}',
    '#pes-powerline-panel .pes-pl-chip:hover{border-color:' + GREEN + ';background:#eef9f4;}',
    '#pes-powerline-panel .pes-pl-cta{display:block;width:100%;border:none;border-radius:10px;',
    'background:' + GREEN + ';color:#fff;font-family:inherit;font-size:14px;font-weight:600;',
    'padding:12px;cursor:pointer;transition:background .15s;}',
    '#pes-powerline-panel .pes-pl-cta:hover{background:#0da06c;}',
    '#pes-powerline-panel .pes-pl-foot{padding:0 18px 12px;font-size:10.5px;color:#8a919c;text-align:center;}'
  ].join('\n');

  // ------------------------------------------------------------------
  // DOM
  // ------------------------------------------------------------------
  var panelOpen = false;
  var launcherEl = null;
  var panelEl = null;

  function openPanel() {
    if (!panelEl || panelOpen) return;
    panelOpen = true;
    panelEl.classList.add('pes-pl-open');
    launcherEl.setAttribute('aria-expanded', 'true');
    trackOpen();
  }

  function closePanel() {
    if (!panelEl || !panelOpen) return;
    panelOpen = false;
    panelEl.classList.remove('pes-pl-open');
    if (launcherEl) launcherEl.setAttribute('aria-expanded', 'false');
  }

  function chip(label, action) {
    var b = d.createElement('button');
    b.type = 'button';
    b.className = 'pes-pl-chip';
    b.textContent = label;
    b.addEventListener('click', function () {
      trackAction(action, label);
      if (action === 'quote') {
        w.location.href = QUOTE_URL;
      } else if (action === 'track') {
        openMessenger('Hi — I\'d like to track my order.\n\nOrder number (if known): ');
      } else if (action === 'sizing') {
        openMessenger('Hi — I need sizing / compatibility help.\n\n' + productContextLine());
      } else if (action === 'sales') {
        openMessenger('Hi — I\'d like to talk to sales about trade pricing and availability.\n\n' + productContextLine());
      }
    });
    return b;
  }

  function build() {
    if (d.getElementById('pes-powerline-launcher')) return;

    var style = d.createElement('style');
    style.id = 'pes-powerline-styles';
    style.textContent = css;
    d.head.appendChild(style);

    // Launcher (the visible bubble)
    launcherEl = d.createElement('button');
    launcherEl.id = 'pes-powerline-launcher';
    launcherEl.type = 'button';
    launcherEl.setAttribute('aria-label', 'Open PES PowerLine chat');
    launcherEl.setAttribute('aria-expanded', 'false');
    launcherEl.setAttribute('aria-controls', 'pes-powerline-panel');
    launcherEl.innerHTML =
      '<svg class="pes-pl-bolt" viewBox="0 0 24 24"><path d="M13 2 4.5 13.5H11L9.5 22 19 10h-6.5L13 2z"/></svg>' +
      '<span class="pes-pl-dot" aria-hidden="true"></span>';
    launcherEl.addEventListener('click', function () {
      if (panelOpen) { closePanel(); } else { openPanel(); }
    });
    d.body.appendChild(launcherEl);

    // Panel
    panelEl = d.createElement('div');
    panelEl.id = 'pes-powerline-panel';
    panelEl.setAttribute('role', 'dialog');
    panelEl.setAttribute('aria-label', 'PES PowerLine');

    var head = d.createElement('div');
    head.className = 'pes-pl-head';
    head.innerHTML =
      '<img class="pes-pl-logo" src="' + LOGO_URL + '" alt="PES Supply" loading="lazy">' +
      '<p class="pes-pl-title">PES PowerLine</p>' +
      '<p class="pes-pl-sub">Wholesale support — real humans, trade answers.</p>';
    var closeBtn = d.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'pes-pl-close';
    closeBtn.setAttribute('aria-label', 'Close chat panel');
    closeBtn.textContent = '\u00D7';
    closeBtn.addEventListener('click', closePanel);
    head.appendChild(closeBtn);

    var body = d.createElement('div');
    body.className = 'pes-pl-body';

    var greet = d.createElement('p');
    greet.className = 'pes-pl-greet';
    greet.textContent = contextGreeting();
    body.appendChild(greet);

    var chips = d.createElement('div');
    chips.className = 'pes-pl-chips';
    chips.appendChild(chip('Get a quote', 'quote'));
    chips.appendChild(chip('Track my order', 'track'));
    chips.appendChild(chip('Sizing help', 'sizing'));
    chips.appendChild(chip('Talk to sales', 'sales'));
    body.appendChild(chips);

    var cta = d.createElement('button');
    cta.type = 'button';
    cta.className = 'pes-pl-cta';
    cta.textContent = 'Chat with us';
    cta.addEventListener('click', function () {
      trackAction('open_chat', 'Chat with us');
      openMessenger('');
    });
    body.appendChild(cta);

    var foot = d.createElement('div');
    foot.className = 'pes-pl-foot';
    foot.textContent = '169 authorized brands \u00B7 Trade pricing \u00B7 7\u201310 business days nationwide';

    panelEl.appendChild(head);
    panelEl.appendChild(body);
    panelEl.appendChild(foot);
    d.body.appendChild(panelEl);

    // Escape closes
    d.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') closePanel();
    });

    fireBubbleShown();
  }

  // Build when body is ready (script is deferred, but guard anyway)
  if (d.readyState === 'loading') {
    d.addEventListener('DOMContentLoaded', build);
  } else {
    build();
  }

  // SPA route changes: refresh greeting for the new context
  var lastUrl = w.location.href;
  setInterval(function () {
    if (w.location.href !== lastUrl) {
      lastUrl = w.location.href;
      closePanel();
      var greet = d.querySelector('#pes-powerline-panel .pes-pl-greet');
      if (greet) greet.textContent = contextGreeting();
    }
  }, 500);

  // Small public API for other theme code
  w.PESPowerLine = {
    open: openPanel,
    close: closePanel,
    openMessenger: openMessenger
  };
})();
